// The infantry drawn as crowds. A soldier given his own skinned mesh costs the main thread a
// clip mixer, sixty-two bone matrices and a bone texture upload every frame, and ten draw calls
// (his parts, his gun, and all of them again for the shadow): two hundred of them and the frame
// was theirs. Here every soldier of a type is one instance of one mesh. His clips are baked
// once, when the model arrives, into a texture that holds every bone's matrix for every frame
// of every clip; the vertex shader reads the frame he is on, so playing a clip is writing four
// numbers to an instance attribute, and the whole crowd of a type is one draw and one shadow.
// What the mixer did (idle, aim, walk, run, shoot, die, and the fade between them) each
// instance does in the shader: two clips at once, blended by how far the fade has come.
import * as THREE from 'three';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';

/** Baked frames a second: the shader shows the nearest, and at a soldier's size on screen thirty read as smooth. */
const FPS = 30;
/** Instances a crowd of one type can hold. */
const MAX = 1024;
/** How strongly the team colour rims each silhouette. */
const RIM = 0.28;

/** A clip in the texture: its first row, how many rows it has, and how many of those a loop runs over (the last row repeats the first). */
interface Clip { start: number; frames: number; loop: number }

const GLSL_PARS = /* glsl */`
uniform highp sampler2D uBones;
uniform float uTime;
attribute vec4 skinIndex;
attribute vec4 skinWeight;
attribute vec4 aAnimA;
attribute vec4 aAnimB;
attribute vec4 aMix;
mat4 crowdBone(int frame, float bone) {
  int x = int(bone) * 4;
  return mat4(
    texelFetch(uBones, ivec2(x, frame), 0),
    texelFetch(uBones, ivec2(x + 1, frame), 0),
    texelFetch(uBones, ivec2(x + 2, frame), 0),
    texelFetch(uBones, ivec2(x + 3, frame), 0));
}
// a clip: x its first row, y the rows it runs over, z when it started (s), w its rate (rows a second); held on its last row or wrapped
int crowdFrame(vec4 a, float hold) {
  float f = max(0.0, (uTime - a.z) * a.w);
  f = hold > 0.5 ? min(f, a.y - 1.0) : mod(f, a.y);
  return int(a.x) + int(floor(f + 0.5));
}
mat4 crowdSkin(vec4 a, float hold) {
  int fr = crowdFrame(a, hold);
  return skinWeight.x * crowdBone(fr, skinIndex.x) + skinWeight.y * crowdBone(fr, skinIndex.y)
    + skinWeight.z * crowdBone(fr, skinIndex.z) + skinWeight.w * crowdBone(fr, skinIndex.w);
}
// the pose: the clip playing, faded in over the one before it
mat4 crowdMatrix() {
  float w = clamp((uTime - aMix.x) / max(aMix.y, 1e-4), 0.0, 1.0);
  mat4 m = crowdSkin(aAnimA, aMix.z);
  if (w < 1.0) m = m * w + crowdSkin(aAnimB, aMix.w) * (1.0 - w);
  return m;
}
`;

/** The lit material: the crowd's skinning, the baked part colours, and the team's rim light on the top of each silhouette. */
function crowdMaterial(texture: THREE.DataTexture, time: { value: number }): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.72, metalness: 0.05 });
  m.onBeforeCompile = shader => {
    shader.uniforms.uBones = { value: texture };
    shader.uniforms.uTime = time;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${GLSL_PARS}\nattribute vec3 aRim;\nvarying vec3 vRim;`)
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
        mat4 crowdM = crowdMatrix();
        objectNormal = normalize(mat3(crowdM) * objectNormal);
        vRim = aRim;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        transformed = (crowdM * vec4(transformed, 1.0)).xyz;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vRim;')
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        {
          vec3 rimView = isOrthographic ? vec3(0.0, 0.0, 1.0) : normalize(vViewPosition);
          float rim = pow(1.0 - clamp(dot(normal, rimView), 0.0, 1.0), 4.0);
          rim *= smoothstep(0.05, 0.6, inverseTransformDirection(normal, viewMatrix).y);
          totalEmissiveRadiance += vRim * rim * ${RIM.toFixed(2)};
        }`);
  };
  m.customProgramCacheKey = () => 'crowd';
  return m;
}

/** The shadow pass draws the crowd through this: the same skinning, into the sun's depth map. */
function crowdDepthMaterial(texture: THREE.DataTexture, time: { value: number }): THREE.MeshDepthMaterial {
  const m = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  m.onBeforeCompile = shader => {
    shader.uniforms.uBones = { value: texture };
    shader.uniforms.uTime = time;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${GLSL_PARS}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        transformed = (crowdMatrix() * vec4(transformed, 1.0)).xyz;`);
  };
  m.customProgramCacheKey = () => 'crowd-depth';
  return m;
}

/**
 * Bakes a loaded infantry model: its parts into one geometry with the part colours on the
 * vertices and the gun bound to the hand that holds it, and its clips into the bone texture.
 */
function bake(model: THREE.Object3D): { geometry: THREE.BufferGeometry; texture: THREE.DataTexture; clips: Map<string, Clip> } {
  // baked on a copy, so the template every other look clones stays at rest
  const root = cloneSkinned(model);
  root.position.set(0, 0, 0); root.rotation.set(0, 0, 0); root.scale.setScalar(1);
  root.updateMatrixWorld(true);
  let skeleton: THREE.Skeleton | null = null;
  root.traverse(o => { if ((o as THREE.SkinnedMesh).isSkinnedMesh && !skeleton) skeleton = (o as THREE.SkinnedMesh).skeleton; });
  if (!skeleton) throw new Error('crowd: model has no skin');
  const bones = (skeleton as THREE.Skeleton).bones;
  const boneIndex = new Map(bones.map((b, i) => [b as THREE.Object3D, i]));
  // one more "bone" after the real ones that never moves, and the chest bone for packs on the back
  const STILL = bones.length;
  const chestIdx = bones.findIndex(b => b.name === 'Chest');

  // ---- geometry ----
  const pos: number[] = [], nor: number[] = [], col: number[] = [], idx: number[] = [], sIdx: number[] = [], sW: number[] = [];
  const c = new THREE.Color();
  const v = new THREE.Vector3();
  const nm = new THREE.Matrix3();
  root.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const geo = mesh.geometry;
    const skinned = (mesh as THREE.SkinnedMesh).isSkinnedMesh;
    const mats = [mesh.material as THREE.Material | THREE.Material[]].flat();
    const p = geo.attributes.position as THREE.BufferAttribute, n = geo.attributes.normal as THREE.BufferAttribute;
    const base = pos.length / 3;
    // a part that is not skinned rides the bone it hangs from (the gun in the hand), or the chest if fixed to the torso
    let bone = chestIdx >= 0 ? chestIdx : STILL;
    if (!skinned) {
      for (let a: THREE.Object3D | null = mesh.parent; a; a = a.parent) { const i = boneIndex.get(a); if (i !== undefined) { bone = i; break; } }
      nm.getNormalMatrix(mesh.matrixWorld);
    }
    const si = geo.attributes.skinIndex as THREE.BufferAttribute | undefined, sw = geo.attributes.skinWeight as THREE.BufferAttribute | undefined;
    // which material each vertex wears, by the geometry's groups
    const groupOf = new Int32Array(p.count);
    for (const g of geo.groups) { const ix = geo.index; for (let k = g.start; k < g.start + g.count; k++) groupOf[ix ? ix.getX(k) : k] = g.materialIndex ?? 0; }
    for (let i = 0; i < p.count; i++) {
      v.fromBufferAttribute(p, i);
      if (!skinned) v.applyMatrix4(mesh.matrixWorld);
      pos.push(v.x, v.y, v.z);
      v.fromBufferAttribute(n, i);
      if (!skinned) v.applyMatrix3(nm).normalize();
      nor.push(v.x, v.y, v.z);
      const mat = (mats[groupOf[i]!] ?? mats[0]) as THREE.MeshStandardMaterial;
      c.copy(mat.color ?? new THREE.Color(0xffffff));
      col.push(c.r, c.g, c.b);
      if (skinned && si && sw) { sIdx.push(si.getX(i), si.getY(i), si.getZ(i), si.getW(i)); sW.push(sw.getX(i), sw.getY(i), sw.getZ(i), sw.getW(i)); }
      else { sIdx.push(bone, 0, 0, 0); sW.push(1, 0, 0, 0); }
    }
    const ix = geo.index;
    if (ix) for (let k = 0; k < ix.count; k++) idx.push(base + ix.getX(k));
    else for (let k = 0; k < p.count; k++) idx.push(base + k);
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geometry.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(sIdx, 4));
  geometry.setAttribute('skinWeight', new THREE.Float32BufferAttribute(sW, 4));
  geometry.setIndex(idx);
  geometry.computeBoundingSphere();

  // ---- the clips ----
  const clips = new Map<string, Clip>();
  let rows = 0;
  for (const clip of model.animations) {
    const frames = Math.max(1, Math.round(clip.duration * FPS)) + 1;
    clips.set(clip.name.split('|').pop()!, { start: rows, frames, loop: frames - 1 });
    rows += frames;
  }
  const width = (bones.length + 1) * 4;
  const data = new Float32Array(width * 4 * rows);
  const mixer = new THREE.AnimationMixer(root);
  const m = new THREE.Matrix4();
  for (const clip of model.animations) {
    const { start, frames } = clips.get(clip.name.split('|').pop()!)!;
    const action = mixer.clipAction(clip);
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.play();
    for (let f = 0; f < frames; f++) {
      mixer.setTime(Math.min(clip.duration, f / FPS));
      root.updateMatrixWorld(true);
      const row = (start + f) * width * 4;
      for (let i = 0; i < bones.length; i++) {
        m.multiplyMatrices(bones[i]!.matrixWorld, (skeleton as THREE.Skeleton).boneInverses[i]!);
        data.set(m.elements, row + i * 16);
      }
      data.set(m.identity().elements, row + STILL * 16);
    }
    action.stop();
    mixer.uncacheAction(clip);
  }
  mixer.uncacheRoot(root);
  const texture = new THREE.DataTexture(data, width, rows, THREE.RGBAFormat, THREE.FloatType);
  texture.minFilter = texture.magFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return { geometry, texture, clips };
}

/** Every soldier of one type: one instanced mesh, its instance attributes, and who holds each slot. */
class Kind {
  readonly mesh: THREE.InstancedMesh;
  readonly animA: THREE.InstancedBufferAttribute;
  readonly animB: THREE.InstancedBufferAttribute;
  readonly mix: THREE.InstancedBufferAttribute;
  readonly rim: THREE.InstancedBufferAttribute;
  readonly clips: Map<string, Clip>;
  readonly members: CrowdMember[] = [];

  constructor(model: THREE.Object3D, time: { value: number }) {
    const { geometry, texture, clips } = bake(model);
    this.clips = clips;
    const attr = (n: number) => { const a = new THREE.InstancedBufferAttribute(new Float32Array(MAX * n), n); a.setUsage(THREE.DynamicDrawUsage); return a; };
    geometry.setAttribute('aAnimA', this.animA = attr(4));
    geometry.setAttribute('aAnimB', this.animB = attr(4));
    geometry.setAttribute('aMix', this.mix = attr(4));
    geometry.setAttribute('aRim', this.rim = attr(3));
    this.mesh = new THREE.InstancedMesh(geometry, crowdMaterial(texture, time), MAX);
    this.mesh.customDepthMaterial = crowdDepthMaterial(texture, time);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.castShadow = this.mesh.receiveShadow = true;
    // the crowd is all over the field; its bounds would need working out again every frame
    this.mesh.frustumCulled = false;
  }

  /** The slot a new member takes: the next after the last in use. */
  take(member: CrowdMember): number {
    this.members.push(member);
    this.mesh.count = this.members.length;
    return this.members.length - 1;
  }

  /** Frees a slot: the last member moves into it, so the ones in use stay packed at the front. */
  free(slot: number): void {
    const last = this.members.pop()!;
    if (last.slot !== slot) {
      this.members[slot] = last;
      last.slot = slot;
      const from = this.members.length;
      for (const a of [this.animA, this.animB, this.mix, this.rim]) {
        const n = a.itemSize;
        (a.array as Float32Array).copyWithin(slot * n, from * n, from * n + n);
        a.needsUpdate = true;
      }
      (this.mesh.instanceMatrix.array as Float32Array).copyWithin(slot * 16, from * 16, from * 16 + 16);
    }
    this.mesh.count = this.members.length;
  }
}

/** One soldier in a crowd: where he stands, what he is doing, and his side's colour. */
export class CrowdMember {
  slot: number;
  private current: string | null = null;
  private rate = FPS;
  private held = false;

  constructor(private readonly kind: Kind, private readonly crowd: Crowd) {
    this.slot = kind.take(this);
    this.kind.rim.setXYZ(this.slot, 1, 1, 1);
    this.kind.mix.setXYZW(this.slot, 0, 1, 0, 0);
  }

  /** The clip he is playing. */
  get clip(): string | null {
    return this.current;
  }

  /** Whether the model carries this clip. */
  has(name: string): boolean {
    return this.kind.clips.has(name);
  }

  /** Plays a clip, fading from the one before over `fade` seconds; a held clip stops on its last frame instead of looping. */
  play(name: string, fade: number, hold = false, timeScale = 1): boolean {
    const clip = this.kind.clips.get(name);
    if (!clip) return false;
    const now = this.crowd.seconds;
    const { animA, animB, mix } = this.kind;
    // the clip playing becomes the one faded out
    animB.setXYZW(this.slot, animA.getX(this.slot), animA.getY(this.slot), animA.getZ(this.slot), animA.getW(this.slot));
    mix.setW(this.slot, mix.getZ(this.slot));
    this.rate = FPS * timeScale;
    animA.setXYZW(this.slot, clip.start, hold ? clip.frames : clip.loop, now, this.rate);
    mix.setXYZ(this.slot, now, fade, hold ? 1 : 0);
    animA.needsUpdate = animB.needsUpdate = mix.needsUpdate = true;
    this.current = name;
    this.held = hold;
    return true;
  }

  /** Changes the clip's speed without a jump in the pose: its start is moved so the frame it is on stays. */
  setTimeScale(timeScale: number): void {
    const rate = FPS * timeScale;
    if (Math.abs(rate - this.rate) < 1e-3) return;
    const { animA } = this.kind;
    const now = this.crowd.seconds;
    const f = (now - animA.getZ(this.slot)) * this.rate;
    animA.setZ(this.slot, now - f / rate);
    animA.setW(this.slot, rate);
    animA.needsUpdate = true;
    this.rate = rate;
  }

  /** Moves the clip along by `seconds`, so a squad does not march in step. */
  skip(seconds: number): void {
    const { animA } = this.kind;
    animA.setZ(this.slot, animA.getZ(this.slot) - seconds);
    animA.needsUpdate = true;
  }

  /** Whether a held clip has reached its last frame. */
  get finished(): boolean {
    if (!this.held) return false;
    const { animA } = this.kind;
    return (this.crowd.seconds - animA.getZ(this.slot)) * this.rate >= animA.getY(this.slot) - 1;
  }

  /** The colour that rims his silhouette. */
  setRim(color: number): void {
    const c = new THREE.Color(color);
    this.kind.rim.setXYZ(this.slot, c.r, c.g, c.b);
    this.kind.rim.needsUpdate = true;
  }

  /** Where he stands and which way he faces (his group's matrix), and how big the model is drawn. */
  setMatrix(m: THREE.Matrix4, scale: number): void {
    const a = this.kind.mesh.instanceMatrix.array as Float32Array;
    const e = m.elements, o = this.slot * 16;
    for (let i = 0; i < 12; i++) a[o + i] = e[i]! * scale;
    a[o + 12] = e[12]!; a[o + 13] = e[13]!; a[o + 14] = e[14]!; a[o + 15] = 1;
  }

  /** He is gone: his slot goes to the last member of the crowd. */
  release(): void {
    if (this.slot < 0) return;
    this.kind.free(this.slot);
    this.slot = -1;
  }
}

/** The crowds of every infantry type, and the clock their clips run on. */
export class Crowd {
  readonly group = new THREE.Group();
  private readonly kinds = new Map<string, Kind>();
  private readonly time = { value: 0 };
  private readonly epoch = performance.now();

  /** Seconds on the crowd's clock, which the clips' starts are kept in. */
  get seconds(): number {
    return this.time.value;
  }

  /** A new member of the crowd of `type`, baked from `model` the first time the type is seen; null for a model with no skin. */
  member(type: string, model: THREE.Object3D): CrowdMember | null {
    let kind = this.kinds.get(type);
    if (!kind) {
      try { kind = new Kind(model, this.time); } catch { return null; }
      this.kinds.set(type, kind);
      this.group.add(kind.mesh);
    }
    return new CrowdMember(kind, this);
  }

  /** Advances the clock and sends the frame's instance data up. */
  update(now: number): void {
    this.time.value = (now - this.epoch) / 1000;
    for (const k of this.kinds.values()) if (k.mesh.count > 0) k.mesh.instanceMatrix.needsUpdate = true;
  }
}
